import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X, Smartphone, Download, Volume2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { playNotificationSound } from "@/services/notificationService";

// Store the install prompt globally
let deferredPrompt = null;

// Listen for beforeinstallprompt event
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    console.log('PWA install prompt captured');
  });
}

const AppDownloadPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const location = useLocation();
  
  // Pages where popup should NOT appear (auth pages and user dashboards)
  const excludedPathPrefixes = ['/login', '/register', '/dashboard', '/admin', '/support'];
  const isExcludedPage = excludedPathPrefixes.some(prefix => location.pathname.startsWith(prefix));

  useEffect(() => {
    // Don't show on excluded pages (login, register)
    if (isExcludedPage) {
      setShowSplash(false);
      setIsVisible(false);
      return;
    }
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      setShowSplash(false);
      return;
    }

    // Check if install prompt is available
    const checkInstallable = () => {
      setCanInstall(!!deferredPrompt);
    };
    checkInstallable();
    const interval = setInterval(checkInstallable, 1000);

    // Check if user has dismissed the popup before
    const dismissed = localStorage.getItem("appPopupDismissed");
    const dismissedAt = localStorage.getItem("appPopupDismissedAt");
    
    // Show popup again after 7 days
    if (dismissed && dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        setIsDismissed(true);
        setShowSplash(false);
        return () => clearInterval(interval);
      }
    }

    // Hide splash after animation
    const splashTimer = setTimeout(() => {
      setShowSplash(false);
    }, 2500);

    // Show popup after splash ends
    const timer = setTimeout(() => {
      if (!isDismissed) {
        setIsVisible(true);
      }
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(splashTimer);
      clearInterval(interval);
    };
  }, [isDismissed, isExcludedPage]);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem("appPopupDismissed", "true");
    localStorage.setItem("appPopupDismissedAt", Date.now().toString());
  };

  const handleDownload = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('User response to install prompt:', outcome);
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      deferredPrompt = null;
      setCanInstall(false);
    } else {
      // Fallback for iOS or when prompt isn't available
      alert('To download the app:\n\n' +
        'iPhone/iPad: Tap Share → "Add to Home Screen"\n\n' +
        'Android: Tap menu (⋮) → "Install App"');
    }
    handleDismiss();
  };

  const handlePreviewSound = () => {
    playNotificationSound();
  };

  // Splash Screen - Don't show on excluded pages
  if (showSplash && !isDismissed && !isInstalled && !isExcludedPage) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 bottom-0 w-full h-full z-[102] flex items-center justify-center bg-obsidian"
        style={{ margin: 0, padding: 0 }}
        data-testid="splash-screen"
      >
        {/* Simple background with subtle glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian to-obsidian-light" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-electric/10 rounded-full blur-3xl animate-[pulse_3s_ease-in-out_infinite]" />
        
        {/* Center logo with entrance animation */}
        <div className="relative z-10 flex flex-col items-center animate-[fadeScaleIn_0.8s_ease-out_forwards]">
          <img 
            src="/app-icon-wide.png" 
            alt="BrownStone Jazz Club" 
            className="w-28 h-28 rounded-2xl shadow-lg shadow-electric/30"
          />
          <div className="mt-6 relative py-4 w-48" style={{ fontFamily: "'Cookie', cursive" }}>
            <span className="absolute top-0 left-4 text-white/60 text-sm">The</span>
            <p className="text-2xl text-center inline-flex items-end justify-center w-full">
              <span className="text-platinum relative -top-2">Brown</span>
              <span className="text-electric inline-block -mx-0.5" style={{ transform: 'scaleX(-1.3)', fontFamily: 'serif' }}>𝄞</span>
              <span className="text-platinum relative top-1">tone</span>
            </p>
            <span className="absolute bottom-0 right-2 text-white/60 text-sm whitespace-nowrap">Jazz Club</span>
          </div>
        </div>

        {/* CSS for animation */}
        <style>{`
          @keyframes fadeScaleIn {
            0% {
              opacity: 0;
              transform: scale(0.8);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}</style>
      </div>
    );
  }

  if (!isVisible || isDismissed || isInstalled) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-in fade-in duration-300"
        onClick={handleDismiss}
        data-testid="app-popup-backdrop"
      />
      
      {/* Popup */}
      <div 
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-[90%] max-w-md animate-in zoom-in-95 fade-in duration-300"
        data-testid="app-download-popup"
      >
        <div className="relative bg-gradient-to-br from-obsidian-light to-obsidian border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
          {/* Electric blue accent glow */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-electric/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-electric/10 rounded-full blur-3xl" />
          
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors z-10"
            data-testid="app-popup-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="relative p-8 text-center">
            {/* App Icon - using widened treble clef */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <img 
                  src="/app-icon-wide.png" 
                  alt="BrownStone App" 
                  className="w-24 h-24 rounded-2xl shadow-lg shadow-electric/30"
                />
                {/* App badge */}
                <div className="absolute -bottom-2 -right-2 bg-electric rounded-full p-1.5">
                  <Smartphone className="w-4 h-4 text-obsidian" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h3 className="font-display text-2xl text-white mb-2">
              Get the <span className="text-platinum">Brown</span><span className="text-electric inline-block -mx-0.5" style={{ transform: 'scaleX(-1.3)' }}>𝄞</span><span className="text-platinum">tone</span> App
            </h3>
            
            {/* Description */}
            <p className="text-white/60 text-sm mb-4 max-w-xs mx-auto leading-relaxed">
              Never miss a show. Get exclusive access to events, connect with the jazz community, 
              and unlock member-only perks.
            </p>

            {/* Sound Preview Button */}
            <button
              onClick={handlePreviewSound}
              className="inline-flex items-center gap-2 text-electric text-sm mb-4 hover:text-electric-light transition-colors"
              data-testid="preview-sound-btn"
            >
              <Volume2 className="w-4 h-4" />
              <span>Preview notification sound</span>
            </button>

            {/* Features */}
            <div className="flex justify-center gap-4 mb-6">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-1">
                  <span className="text-lg">🎵</span>
                </div>
                <span className="text-white/40 text-xs">Events</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-1">
                  <span className="text-lg">🎫</span>
                </div>
                <span className="text-white/40 text-xs">Tickets</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-1">
                  <span className="text-lg">👥</span>
                </div>
                <span className="text-white/40 text-xs">Community</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={handleDownload}
                className="bg-electric text-obsidian hover:bg-electric-light font-medium px-6 py-5 gap-2"
                data-testid="app-popup-download"
              >
                <Download className="w-4 h-4" />
                {canInstall ? 'Download App' : 'Get the App'}
              </Button>
              <Button 
                onClick={handleDismiss}
                variant="ghost"
                className="text-white/50 hover:text-white hover:bg-white/5 px-6 py-5"
                data-testid="app-popup-later"
              >
                Maybe Later
              </Button>
            </div>

            {/* Install hint */}
            <p className="text-white/30 text-xs mt-4">
              {canInstall ? 'Free download - works offline' : 'Available on all devices'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppDownloadPopup;
